<template>
  <div>
    <h3 class="mt-4">Connected Wallets</h3>
    <div class="mb-3" v-if="wallets.length > 0">
      <strong style="margin-right:15px">Total Stake: {{ formatStake(totalStake) }} SOL</strong>
      <el-button
        class="ml-3 btn-outline-info"
        size="small"
        type="info"
        @click="refreshStakes"
        :loading="isRefreshing"
      >
        <el-icon><Refresh /></el-icon>
      </el-button>
      <small class="d-block text-muted mt-1">Stakes are automatically updated every 15 minutes</small>
    </div>
      <div v-else>
          <div class="card">
              <div class="card-header">
                  <h4>You have no wallets connected yet</h4>
              </div>
              <div class="card-body">
                  <p class="lead">To get more voting power on the site, please connect your wallet and sign a message to verify ownership. Verification is free</p>
                  <ol class="lead">
                      <li>Choose your wallet and connect to Solana Compass</li>
                      <li>Click 'Sign message to verify wallet' and your wallet will be associated with your Compass account</li>
                  </ol>
                  <p class="lead">The more stake to Solana Compass you have in your wallet - whether in a stake account or as compassSOL - the more weight your votes have. Stake totals are refreshed every 15 minutes.</p>
              </div>
          </div>
      </div>
    <ul id="wallet-list" class="list-unstyled">
      <li v-for="wallet in wallets" :key="wallet.address" class="mb-2 p-2 border rounded">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <div class="text-monospace">{{ wallet.address }}</div>
            <small class="text-muted">
              Native Stake: {{ formatStake(wallet.native_stake) }} SOL
              <br>
              Liquid Stake: {{ formatStake(wallet.liquid_stake) }} SOL
              <br>
              Total: {{ formatStake(wallet.total_stake) }} SOL
            </small>
          </div>
          <button class="btn btn-sm btn-danger" @click="removeWallet(wallet.address)">Remove</button>
        </div>
      </li>
    </ul>
    <div class="text-muted" v-if="wallets.length > 0">
        Want to attach more wallets to your account and increase your voting power? Simply disconnect your current wallet then connect and sign again with a new wallet. You can attach as many wallets as you like.
    </div>
    <el-button
      type="primary"
      @click="connectWallet"
    >
      {{ isWalletConnected ? 'Sign message to verify ' + (wallet?.address || 'wallet') : 'Click to connect your wallet' }}
    </el-button>
      <WalletModalProvider ref="walletMod">
          <template #default="modalScope">
              <div @click="modalScope.openModal()" ref="trigger"></div>
          </template>
      </WalletModalProvider>
  </div>
</template>

<script>
import {ref, computed, watch, onMounted} from 'vue';
import { initWallet, useWallet, WalletModalProvider } from 'solana-wallets-vue';
import { ElMessage } from 'element-plus';
import { Refresh } from '@element-plus/icons-vue';

export default {
  name: 'WalletConnection',
  props: {
    initialWallets: Array,
  },
    components:{
        WalletModalProvider,
        Refresh
    },
  setup(props) {
    const wallets = ref(props.initialWallets);
    const { connected, publicKey, wallet } = useWallet();
    const isRefreshing = ref(false);
    const totalStake = ref(props.initialWallets.reduce((sum, wallet) => sum + wallet.total_stake, 0));
    const isWalletConnected = ref(false);
  const trigger = ref(null); // Create a ref for the trigger element
    // Watch for wallet connection changes
    watch(connected, (newValue) => {
      isWalletConnected.value = newValue;
    }, { immediate: true });

    const formatStake = (lamports) => {
      return (lamports / 1e9).toFixed(2);
    };

    const refreshStakes = async () => {
      isRefreshing.value = true;
      try {
        const response = await fetch('/account/refresh-stakes', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
          }
        });

        if (response.ok) {
          const data = await response.json();
          wallets.value = data.wallets;
          totalStake.value = data.total_stake * 1e9; // Convert back to lamports
        } else {
          alert('Failed to refresh stakes');
        }
      } catch (error) {
        console.error('Error refreshing stakes:', error);
        alert('Failed to refresh stakes');
      } finally {
        isRefreshing.value = false;
      }
    };

    const connectWallet = async () => {
        console.log('connectWallet',isWalletConnected, trigger)
        if (!isWalletConnected.value) {
            console.log(trigger)
            trigger.value.click()
            return;
        }
      try {
        const response = await fetch('/wallet/message');
        const { message } = await response.json();

        const encodedMessage = new TextEncoder().encode(message);
        const signedMessage = await window.solana.signMessage(encodedMessage, 'utf8');
        console.log(publicKey, signedMessage)
        const connectResponse = await fetch('/wallet/connect', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
          },
          body: JSON.stringify({
            message: message,
            signature: Buffer.from(signedMessage.signature).toString('hex'),
            publicKey: Buffer.from(publicKey.value.toBytes()).toString('hex'),
            //  Array.from(signedMessage.signature, (b) => b.toString(16).padStart(2, '0')).join('')

          })
        });

        const result = await connectResponse.json();
        if (connectResponse.ok) {
          ElMessage.success('Wallet connected successfully!');
          wallets.value.push(result.wallet);
          totalStake.value += result.wallet.total_stake;
        } else {
            console.log(result)
          ElMessage.error(result.error || 'Failed to connect wallet');
        }
      } catch (error) {
        console.error('Error connecting wallet:', error);
        ElMessage.error('Failed to connect wallet');
      }
    };

    const removeWallet = async (address) => {
      if (confirm('Are you sure you want to remove this wallet?')) {
        try {
          const response = await fetch('/wallet/remove', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
            },
            body: JSON.stringify({ address })
          });

          if (response.ok) {
            wallets.value = wallets.value.filter(wallet => wallet.address !== address);
            ElMessage.success('Wallet removed successfully');
          } else {
            ElMessage.error('Failed to remove wallet');
          }
        } catch (error) {
          console.error('Error removing wallet:', error);
          ElMessage.error('Failed to remove wallet');
        }
      }
    };

    onMounted( async () => {
        console.log('onMounted')
        isWalletConnected.value = connected.value;
        console.log('onMounted',wallet, wallet.value?.connected, wallet.connected?.value, connected.value, isWalletConnected.value)
    })
    return {
      wallets,
      connectWallet,
      removeWallet,
      refreshStakes,
      isRefreshing,
      formatStake,
      totalStake,
      isWalletConnected,
      trigger
    };
  },
};
</script>
